import {registerBrick, Service} from 'olympe';
import {ReactBrick} from '@olympeio/core';
import {MAX_EXPECTED_REPLICAS, SHARED_MEMORY_TOPICS} from '../services/service_api/CreateAllRASharedMemory.brick';
import {SUBMIT_PR_SHARED_MEMORY_TOPICS} from '../services/service_api/CreateSubmitPRSharedMemory.brick';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Button, Checkbox, Chip, CircularProgress, FormControlLabel, TextField, ToggleButton, ToggleButtonGroup} from '@mui/material';

/**
 * @typedef {Object} LabelValue
 * @property {string} label
 * @property {string} value
 */

/**
 * @typedef {Object} DeliveryAddress
 * @property {string} name
 * @property {string} name2
 * @property {string} houseNumber
 * @property {string} street
 * @property {string} postalCode
 * @property {string} city
 * @property {?string} state
 * @property {string} country
 * @property {boolean} manuallyModified
 */

/**
 * @typedef {Object} CxmlBuyerCookie
 * @property {string} userId
 * @property {string} supplierCode
 * @property {string} supplierName
 * @property {string} companyCode
 * @property {string} purchaseOrg
 * @property {LabelValue} plant
 * @property {DeliveryAddress} deliveryAddress
 * @property {string} source
 * @property {LabelValue} materialCode
 * @property {string} l3Category
 * @property {?string} existingEPRID
 */

/**
 * @typedef {Object} SharedMemoryStatusAllRaData
 * @property {string} backendId
 * @property {Map<string, string>} candexSessionIds
 * @property {Map<string, CxmlBuyerCookie>} cxmlBuyerCookies
 * @property {Map<string, string>} systemAliasByUser
 * @property {Map<string, object>} nestBuySessionInfo
 */

/**
 * Reply of the ALL_MEMORIES_ALL_BACKENDS topic: the snapshot of every backend that answered the coordinator.
 *
 * @typedef {Object} AllBackendsSharedMemoryData
 * @property {string} coordinatorId backend that received the request and gathered the others' replies
 * @property {number} expectedReplicas sanitized hint sent by the frontend
 * @property {boolean} complete whether `expectedReplicas` backends answered before the collection timeout
 * @property {Map<string, SharedMemoryStatusAllRaData>} backends snapshots keyed by backend id
 */

/**
 * @typedef {Object} SubmitPRSharedMemoryData
 * @property {Set<string>} prsBeingSubmitted
 */

/**
 * Names of the Map memories held by every backend, in display order.
 * @type {Array<string>}
 */
const ALL_RA_MEMORY_NAMES = ['systemAliasByUser', 'candexSessionIds', 'cxmlBuyerCookies', 'nestBuySessionInfo'];

/**
 * Default value of the "expected replicas" field (production runs between 2 and 5 replicas).
 * @type {number}
 */
const DEFAULT_EXPECTED_REPLICAS = 2;

/**
 * @typedef {Object} FetchState
 * @property {boolean} loading
 * @property {?string} error
 * @property {?Object} data
 * @property {?Date} fetchedAt
 */

/**
 * Number of map entries / set values rendered per "Show more" step.
 * Keeps the DOM small even when `systemAliasByUser` holds ~1000 users.
 * @type {number}
 */
const PAGE_SIZE = 50;

/**
 * Normalize a map-like value (Map, plain object, or array of [key, value] pairs)
 * into an array of [key, value] entries. The service transport should preserve
 * `Map`, but we stay defensive in case it gets serialized to a plain object.
 *
 * @param {*} raw
 * @return {Array<[string, *]>}
 */
const toEntries = (raw) => {
    if (!raw) {
        return [];
    }
    if (raw instanceof Map) {
        return Array.from(raw.entries());
    }
    if (Array.isArray(raw)) {
        return raw.filter((pair) => Array.isArray(pair) && pair.length === 2);
    }
    if (typeof raw === 'object') {
        return Object.entries(raw);
    }
    return [];
};

/**
 * Normalize a set-like value (Set, array, or plain object used as a set) into an array of values.
 *
 * @param {*} raw
 * @return {Array<*>}
 */
const toList = (raw) => {
    if (!raw) {
        return [];
    }
    if (raw instanceof Set) {
        return Array.from(raw.values());
    }
    if (Array.isArray(raw)) {
        return raw;
    }
    if (typeof raw === 'object') {
        return Object.values(raw);
    }
    return [];
};

/**
 * JSON.stringify replacer converting Map and Set into plain structures so that nested
 * collections are displayed instead of being serialized as `{}`.
 *
 * @param {string} key
 * @param {*} value
 * @return {*}
 */
const collectionReplacer = (key, value) => {
    if (value instanceof Map) {
        return Object.fromEntries(value.entries());
    }
    if (value instanceof Set) {
        return Array.from(value.values());
    }
    return value;
};

/**
 * Stringify any value for display or search purposes.
 *
 * @param {*} value
 * @param {boolean} [pretty=false] whether to indent the JSON output
 * @return {string}
 */
const stringify = (value, pretty = false) => {
    if (value === null || value === undefined) {
        return '-';
    }
    if (typeof value !== 'object') {
        return String(value);
    }
    try {
        return JSON.stringify(value, collectionReplacer, pretty ? 2 : 0);
    } catch {
        // Circular structures or exotic objects: fall back to the default string conversion
        return String(value);
    }
};

/**
 * Format a date as a short local time string (HH:MM:SS) for the "fetched at" indicator.
 *
 * @param {?Date} date
 * @return {string}
 */
const formatTime = (date) => (date ? date.toLocaleTimeString() : '-');

/**
 * React hook wrapping an async fetch function into a {@link FetchState}, with a trigger callback.
 * Guards against state updates after the component unmounted.
 *
 * @param {function(...*): Promise<Object>} fetchFn function returning the data to store
 * @return {[FetchState, function(...*): void]} current state and a function triggering a new fetch
 *   (its arguments are forwarded to `fetchFn`)
 */
const useFetchState = (fetchFn) => {
    const [state, setState] = useState(/** @type {FetchState} */ ({loading: false, error: null, data: null, fetchedAt: null}));
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const trigger = useCallback((...args) => {
        setState((prev) => ({...prev, loading: true, error: null}));
        fetchFn(...args)
            .then((data) => {
                if (mountedRef.current) {
                    setState({loading: false, error: null, data, fetchedAt: new Date()});
                }
            })
            .catch((e) => {
                if (mountedRef.current) {
                    setState((prev) => ({...prev, loading: false, error: e?.message || String(e)}));
                }
            });
    }, [fetchFn]);

    return [state, trigger];
};

/**
 * "Showing X of Y / Show more / Show all" controls. Renders nothing when everything is already visible.
 *
 * @param {{shown: number, total: number, onShowMore: function(): void, onShowAll: function(): void}} props
 * @return {?JSX.Element}
 */
const PaginationControls = ({shown, total, onShowMore, onShowAll}) => {
    if (total <= shown) {
        return null;
    }
    return (
        <div style={styles.paginationRow}>
            <span style={styles.muted}>Showing {shown} of {total}</span>
            <Button size="small" variant="text" onClick={onShowMore}>Show {Math.min(PAGE_SIZE, total - shown)} more</Button>
            <Button size="small" variant="text" onClick={onShowAll}>Show all</Button>
        </div>
    );
};

/**
 * Table header cell for a backend id. The full id is displayed when the column is wide enough; otherwise the
 * fixed table layout truncates it with an ellipsis, and the full id stays available in the tooltip.
 *
 * @param {{backendId: string}} props
 * @return {JSX.Element}
 */
const BackendHeaderCell = ({backendId}) => (
    <th style={styles.thBackend} title={backendId}>
        <span style={styles.backendPillTruncate}>{backendId}</span>
    </th>
);

/**
 * Collapsible card used for each shared memory section (one per Map / Set).
 * Collapsed by default: the user expands what they want to inspect.
 *
 * @param {{title: string, count: number, children: React.ReactNode, actions?: React.ReactNode}} props
 * @return {JSX.Element}
 */
const Section = ({title, count, children, actions}) => {
    const [open, setOpen] = useState(false);

    return (
        <div style={styles.section}>
            <div style={styles.sectionHeader}>
                <button type="button" style={styles.sectionToggle} onClick={() => setOpen((o) => !o)}>
                    <span style={styles.chevron}>{open ? '▾' : '▸'}</span>
                    <span>{title}</span>
                    <span style={styles.countBadge}>{count}</span>
                </button>
                {open && actions}
            </div>
            {open && children}
        </div>
    );
};

/**
 * Display a single value stored in a Map: primitives inline, objects as a two-column field list
 * (nested objects pretty-printed). Generic on purpose so unknown shapes (e.g. nestBuySessionInfo) still render.
 *
 * @param {{value: *}} props
 * @return {JSX.Element}
 */
const ValueView = ({value}) => {
    const [showRaw, setShowRaw] = useState(false);

    if (value === null || value === undefined || typeof value !== 'object') {
        return <span style={styles.primitive}>{stringify(value)}</span>;
    }

    const fields = toEntries(value);

    return (
        <div style={styles.objectView}>
            <div style={styles.fieldGrid}>
                {fields.map(([fieldKey, fieldValue]) => (
                    <React.Fragment key={fieldKey}>
                        <span style={styles.fieldLabel}>{fieldKey}</span>
                        {fieldValue !== null && typeof fieldValue === 'object' ?
                            <pre style={styles.pre}>{stringify(fieldValue, true)}</pre> :
                            <span style={styles.fieldValue}>{stringify(fieldValue)}</span>}
                    </React.Fragment>
                ))}
            </div>
            <button type="button" style={styles.linkButton} onClick={() => setShowRaw((s) => !s)}>
                {showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
            </button>
            {showRaw && <pre style={styles.pre}>{stringify(value, true)}</pre>}
        </div>
    );
};

/**
 * Section displaying the content of a Map as a key/value table, with a text filter
 * (matching key or stringified value) and incremental pagination.
 *
 * @param {{title: string, raw: *}} props `raw` is the Map (or map-like) to display
 * @return {JSX.Element}
 */
const MapSection = ({title, raw}) => {
    const [filter, setFilter] = useState('');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const entries = useMemo(() => toEntries(raw), [raw]);

    // Precompute a lowercase searchable string per entry once, rather than on every keystroke.
    const searchable = useMemo(
        () => entries.map(([key, value]) => `${String(key)} ${stringify(value)}`.toLowerCase()),
        [entries],
    );

    const filtered = useMemo(() => {
        const needle = filter.trim().toLowerCase();
        if (!needle) {
            return entries;
        }
        return entries.filter((entry, index) => searchable[index].includes(needle));
    }, [entries, searchable, filter]);

    // Reset pagination whenever the data set or the filter changes.
    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [raw, filter]);

    const visible = filtered.slice(0, visibleCount);

    const actions = (
        <TextField
            size="small"
            placeholder="Filter by key or value…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.filterField}
        />
    );

    return (
        <Section title={title} count={entries.length} actions={actions}>
            {entries.length === 0 && <div style={styles.emptyPill}>No data</div>}
            {entries.length > 0 && filtered.length === 0 && <div style={styles.emptyPill}>No match for “{filter}”</div>}
            {visible.length > 0 && (
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Key</th>
                            <th style={styles.th}>Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map(([key, value]) => (
                            <tr key={String(key)}>
                                <td style={styles.tdKey}>{String(key)}</td>
                                <td style={styles.td}><ValueView value={value}/></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <PaginationControls
                shown={visible.length}
                total={filtered.length}
                onShowMore={() => setVisibleCount((c) => c + PAGE_SIZE)}
                onShowAll={() => setVisibleCount(filtered.length)}
            />
        </Section>
    );
};

/**
 * Section displaying the content of a Set as a list of chips, with a text filter and incremental pagination.
 *
 * @param {{title: string, raw: *}} props `raw` is the Set (or set-like) to display
 * @return {JSX.Element}
 */
const SetSection = ({title, raw}) => {
    const [filter, setFilter] = useState('');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const values = useMemo(() => toList(raw).map((v) => stringify(v)), [raw]);

    const filtered = useMemo(() => {
        const needle = filter.trim().toLowerCase();
        return needle ? values.filter((v) => v.toLowerCase().includes(needle)) : values;
    }, [values, filter]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [raw, filter]);

    const visible = filtered.slice(0, visibleCount);

    const actions = (
        <TextField
            size="small"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.filterField}
        />
    );

    return (
        <Section title={title} count={values.length} actions={actions}>
            {values.length === 0 && <div style={styles.emptyPill}>No data</div>}
            {values.length > 0 && filtered.length === 0 && <div style={styles.emptyPill}>No match for “{filter}”</div>}
            {visible.length > 0 && (
                <div style={styles.chipsWrap}>
                    {visible.map((value, index) => (
                        <Chip key={`${value}-${index}`} label={value} size="small" style={styles.chip}/>
                    ))}
                </div>
            )}
            <PaginationControls
                shown={visible.length}
                total={filtered.length}
                onShowMore={() => setVisibleCount((c) => c + PAGE_SIZE)}
                onShowAll={() => setVisibleCount(filtered.length)}
            />
        </Section>
    );
};

/**
 * Card wrapping one shared memory source (All RA / Submit PR): title, fetch button (with optional extra controls
 * next to it), last fetch time, loading and error indicators, and the sections as children.
 *
 * @param {{title: string, buttonLabel: string, state: FetchState, onFetch: function(): void, controls?: React.ReactNode, children: React.ReactNode}} props
 * @return {JSX.Element}
 */
const MemoryCard = ({title, buttonLabel, state, onFetch, controls, children}) => (
    <div style={styles.card}>
        <div style={styles.cardHeader}>
            <div style={styles.cardTitleRow}>
                <strong>{title}</strong>
                {state.fetchedAt && <span style={styles.muted}>fetched at {formatTime(state.fetchedAt)}</span>}
            </div>
            <div style={styles.cardControls}>
                {controls}
                <Button
                    variant="contained"
                    size="small"
                    disabled={state.loading}
                    onClick={() => onFetch()}
                    startIcon={state.loading ? <CircularProgress size={14} color="inherit"/> : null}
                >
                    {buttonLabel}
                </Button>
            </div>
        </div>
        <div style={styles.body}>
            {state.error && <Alert severity="error">Fetch failed: {state.error}</Alert>}
            {!state.data && !state.loading && !state.error && (
                <div style={styles.emptyState}>Nothing fetched yet. Click “{buttonLabel}”.</div>
            )}
            {state.data && children}
        </div>
    </div>
);

/**
 * Collapsible card displaying the memories of one backend (the four Map sections). Collapsed by default.
 *
 * @param {{backendId: string, data: SharedMemoryStatusAllRaData, isCoordinator: boolean}} props
 * @return {JSX.Element}
 */
const BackendCard = ({backendId, data, isCoordinator}) => {
    const [open, setOpen] = useState(false);
    const total = ALL_RA_MEMORY_NAMES.reduce((sum, name) => sum + toEntries(data?.[name]).length, 0);

    return (
        <div style={styles.backendCard}>
            <button type="button" style={styles.backendHeader} onClick={() => setOpen((o) => !o)}>
                <span style={styles.chevron}>{open ? '▾' : '▸'}</span>
                <span style={styles.backendPill}>{backendId}</span>
                {isCoordinator && <span style={styles.coordinatorTag} title="Backend that received the request and gathered the others">coordinator</span>}
                <span style={styles.muted}>{total} entries</span>
            </button>
            {open && (
                <div style={styles.backendBody}>
                    {ALL_RA_MEMORY_NAMES.map((name) => <MapSection key={name} title={name} raw={data?.[name]}/>)}
                </div>
            )}
        </div>
    );
};

/**
 * Compare the memories across backends: for each memory, the number of entries per backend and whether
 * all backends hold exactly the same keys. Divergence is expected after a backend restart (it starts empty),
 * so this is the main signal this admin view is meant to surface.
 *
 * @param {Array<[string, SharedMemoryStatusAllRaData]>} backends entries [backendId, snapshot]
 * @return {Array<{name: string, counts: Array<number>, inSync: boolean}>} one row per memory, counts in `backends` order
 */
const compareBackends = (backends) => ALL_RA_MEMORY_NAMES.map((name) => {
    const keySets = backends.map(([, data]) => new Set(toEntries(data?.[name]).map(([key]) => String(key))));
    const reference = keySets[0] ?? new Set();
    const inSync = keySets.every((keys) => keys.size === reference.size && [...keys].every((key) => reference.has(key)));
    return {name, counts: keySets.map((keys) => keys.size), inSync};
});

/**
 * Summary table of the multi-backend fetch: how many backends answered vs expected, and per-memory consistency.
 *
 * @param {{result: AllBackendsSharedMemoryData, backends: Array<[string, SharedMemoryStatusAllRaData]>}} props
 * @return {JSX.Element}
 */
const ConsistencySummary = ({result, backends}) => {
    const rows = useMemo(() => compareBackends(backends), [backends]);
    const allInSync = rows.every((row) => row.inSync);

    return (
        <div style={styles.summary}>
            {result.complete ?
                <Alert severity="success">{backends.length} of {result.expectedReplicas} expected backend(s) answered.</Alert> :
                <Alert severity="warning">
                    Only {backends.length} of {result.expectedReplicas} expected backend(s) answered before the collection timeout.
                    Either fewer replicas are running, or one did not answer in time.
                </Alert>}
            {backends.length > 1 && (
                <>
                    {!allInSync && <Alert severity="warning">Some memories differ between backends (see rows marked “differs”).</Alert>}
                    <table style={styles.tableFixed}>
                        <thead>
                            <tr>
                                <th style={styles.thKey}>Memory</th>
                                {backends.map(([backendId]) => <BackendHeaderCell key={backendId} backendId={backendId}/>)}
                                <th style={styles.thStatus}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.name}>
                                    <td style={styles.tdKey}>{row.name}</td>
                                    {row.counts.map((count, index) => <td key={backends[index][0]} style={styles.td}>{count}</td>)}
                                    <td style={styles.td}>
                                        <Chip size="small" label={row.inSync ? 'in sync' : 'differs'} color={row.inSync ? 'success' : 'warning'}/>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
};

/**
 * @typedef {Object} CrossBackendCell
 * @property {boolean} has whether this backend holds the key
 * @property {*} value the value held by this backend (undefined when missing)
 * @property {string} str stringified value, used for equality checks and search
 */

/**
 * @typedef {Object} CrossBackendRow
 * @property {string} key
 * @property {Array<CrossBackendCell>} cells one per backend, in the `backends` order
 * @property {number} presentCount number of backends holding the key
 * @property {'inSync'|'differs'|'missing'} status `missing` when some backends lack the key,
 *   `differs` when all hold it with different values, `inSync` otherwise
 * @property {boolean} hasObject whether at least one value is an object (rendered as ✓ and expandable)
 * @property {string} searchable lowercase text used by the filter
 */

/**
 * Build the key × backend matrix of one memory: for every key held by at least one backend, which backends
 * hold it and whether they agree on the value.
 *
 * @param {string} name memory name (one of ALL_RA_MEMORY_NAMES)
 * @param {Array<[string, SharedMemoryStatusAllRaData]>} backends entries [backendId, snapshot]
 * @return {Array<CrossBackendRow>} rows sorted by key
 */
const buildCrossBackendRows = (name, backends) => {
    const maps = backends.map(([, data]) => new Map(toEntries(data?.[name]).map(([key, value]) => [String(key), value])));
    const keys = [...new Set(maps.flatMap((map) => [...map.keys()]))].sort();

    return keys.map((key) => {
        const cells = maps.map((map) => (map.has(key) ?
            {has: true, value: map.get(key), str: stringify(map.get(key))} :
            {has: false, value: undefined, str: ''}));
        const present = cells.filter((cell) => cell.has);
        const distinctValues = new Set(present.map((cell) => cell.str));

        let status = 'inSync';
        if (present.length < cells.length) {
            status = 'missing';
        } else if (distinctValues.size > 1) {
            status = 'differs';
        }

        return {
            key,
            cells,
            presentCount: present.length,
            status,
            hasObject: present.some((cell) => cell.value !== null && typeof cell.value === 'object'),
            searchable: `${key} ${present.map((cell) => cell.str).join(' ')}`.toLowerCase(),
        };
    });
};

/**
 * Status chip of a cross-backend row.
 *
 * @param {{row: CrossBackendRow, backendCount: number}} props
 * @return {JSX.Element}
 */
const RowStatusChip = ({row, backendCount}) => {
    switch (row.status) {
    case 'missing':
        return <Chip size="small" color="error" label={`${row.presentCount}/${backendCount}`} title="Number of backends holding this key"/>;
    case 'differs':
        return <Chip size="small" color="warning" label="differs"/>;
    default:
        return <Chip size="small" color="success" label="in sync"/>;
    }
};

/**
 * Section showing one memory across all backends: a table with one row per key and one column per backend,
 * telling which backends hold the key (and the value when it is a primitive). Rows holding object values can be
 * expanded to compare the values side by side. Supports text filter, "only differences" filter and pagination.
 *
 * @param {{name: string, backends: Array<[string, SharedMemoryStatusAllRaData]>}} props
 * @return {JSX.Element}
 */
const MemoryAcrossBackendsSection = ({name, backends}) => {
    const [filter, setFilter] = useState('');
    const [onlyDifferences, setOnlyDifferences] = useState(false);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [expandedKeys, setExpandedKeys] = useState(() => new Set());

    const rows = useMemo(() => buildCrossBackendRows(name, backends), [name, backends]);
    const differingCount = useMemo(() => rows.filter((row) => row.status !== 'inSync').length, [rows]);

    const filtered = useMemo(() => {
        const needle = filter.trim().toLowerCase();
        return rows.filter((row) => (!onlyDifferences || row.status !== 'inSync') && (!needle || row.searchable.includes(needle)));
    }, [rows, filter, onlyDifferences]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        setExpandedKeys(new Set());
    }, [backends, filter, onlyDifferences]);

    const visible = filtered.slice(0, visibleCount);

    /**
     * Toggle the expanded state (per-backend values) of a row.
     *
     * @param {string} key
     */
    const toggleExpanded = (key) => setExpandedKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
            next.delete(key);
        } else {
            next.add(key);
        }
        return next;
    });

    /**
     * Style of a value cell depending on presence and agreement between backends.
     *
     * @param {CrossBackendCell} cell
     * @param {CrossBackendRow} row
     * @return {Object}
     */
    const cellStyle = (cell, row) => {
        if (!cell.has) {
            return styles.cellMissing;
        }
        return row.status === 'differs' ? styles.cellDiffers : styles.cellPresent;
    };

    const actions = (
        <div style={styles.sectionActions}>
            <FormControlLabel
                control={<Checkbox size="small" checked={onlyDifferences} onChange={(e) => setOnlyDifferences(e.target.checked)}/>}
                label={<span style={styles.muted}>Only differences ({differingCount})</span>}
            />
            <TextField
                size="small"
                placeholder="Filter by key or value…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={styles.filterField}
            />
        </div>
    );

    return (
        <Section title={name} count={rows.length} actions={actions}>
            {rows.length === 0 && <div style={styles.emptyPill}>No data on any backend</div>}
            {rows.length > 0 && filtered.length === 0 && <div style={styles.emptyPill}>No match</div>}
            {visible.length > 0 && (
                <table style={styles.tableFixed}>
                    <thead>
                        <tr>
                            <th style={styles.thKey}>Key</th>
                            {backends.map(([backendId]) => <BackendHeaderCell key={backendId} backendId={backendId}/>)}
                            <th style={styles.thStatus}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((row) => (
                            <React.Fragment key={row.key}>
                                <tr>
                                    <td style={styles.tdKey}>
                                        {row.hasObject && (
                                            <button type="button" style={styles.rowToggle} onClick={() => toggleExpanded(row.key)} title="Compare values">
                                                {expandedKeys.has(row.key) ? '▾' : '▸'}
                                            </button>
                                        )}
                                        {row.key}
                                    </td>
                                    {row.cells.map((cell, index) => (
                                        <td key={backends[index][0]} style={cellStyle(cell, row)} title={cell.has ? cell.str : 'missing on this backend'}>
                                            {!cell.has && '—'}
                                            {cell.has && (row.hasObject ? '✓' : cell.str)}
                                        </td>
                                    ))}
                                    <td style={styles.td}><RowStatusChip row={row} backendCount={backends.length}/></td>
                                </tr>
                                {expandedKeys.has(row.key) && (
                                    <tr>
                                        <td style={styles.tdExpanded} colSpan={backends.length + 2}>
                                            <div style={styles.perBackendGrid}>
                                                {row.cells.map((cell, index) => (
                                                    <div key={backends[index][0]} style={styles.perBackendBox}>
                                                        <div><span style={styles.backendPill} title={backends[index][0]}>{backends[index][0]}</span></div>
                                                        {cell.has ? <ValueView value={cell.value}/> : <span style={styles.muted}>missing</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            )}
            <PaginationControls
                shown={visible.length}
                total={filtered.length}
                onShowMore={() => setVisibleCount((c) => c + PAGE_SIZE)}
                onShowAll={() => setVisibleCount(filtered.length)}
            />
        </Section>
    );
};

const styles = {
    root: {
        height: '100%',
        overflow: 'auto',
    },
    content: {
        display: 'grid',
        gap: '14px',
        padding: '4px',
    },
    emptyState: {
        border: '1px dashed #cbd5e1',
        borderRadius: '12px',
        padding: '24px',
        textAlign: 'center',
        color: '#475569',
        backgroundColor: '#f8fafc',
    },
    card: {
        border: '1px solid #dbe3ed',
        borderRadius: '12px',
        backgroundColor: '#ffffff',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
        overflow: 'hidden',
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 12px',
        borderBottom: '1px solid #edf2f7',
        backgroundColor: '#f8fafc',
        flexWrap: 'wrap',
    },
    cardTitleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
    },
    cardControls: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
    },
    replicasField: {
        width: '150px',
        backgroundColor: '#ffffff',
    },
    summary: {
        display: 'grid',
        gap: '8px',
    },
    backendCard: {
        border: '1px solid #dbe3ed',
        borderRadius: '10px',
        backgroundColor: '#ffffff',
        overflow: 'hidden',
    },
    backendHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        padding: '8px 10px',
        background: '#f8fafc',
        border: 'none',
        borderBottom: '1px solid #edf2f7',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
    },
    backendBody: {
        display: 'grid',
        gap: '10px',
        padding: '10px',
    },
    coordinatorTag: {
        fontSize: '11px',
        fontWeight: 600,
        padding: '2px 6px',
        borderRadius: '6px',
        color: '#6d28d9',
        backgroundColor: '#ede9fe',
    },
    backendPill: {
        fontSize: '12px',
        fontWeight: 600,
        padding: '4px 8px',
        borderRadius: '999px',
        color: '#1d4ed8',
        backgroundColor: '#dbeafe',
        fontFamily: 'monospace',
    },
    body: {
        display: 'grid',
        gap: '10px',
        padding: '12px',
    },
    section: {
        border: '1px solid #edf2f7',
        borderRadius: '10px',
        padding: '10px',
        backgroundColor: '#fcfdff',
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
        flexWrap: 'wrap',
    },
    sectionToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: '#334155',
        fontWeight: 600,
        fontSize: '13px',
        fontFamily: 'inherit',
    },
    chevron: {
        width: '12px',
        display: 'inline-block',
        color: '#64748b',
    },
    countBadge: {
        minWidth: '24px',
        textAlign: 'center',
        fontSize: '12px',
        fontWeight: 700,
        color: '#1e293b',
        backgroundColor: '#e2e8f0',
        borderRadius: '999px',
        padding: '2px 8px',
    },
    filterField: {
        minWidth: '240px',
        backgroundColor: '#ffffff',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '12px',
    },
    // Fixed layout: columns get a real width, so backend ids can be shown in full when there is room
    // and truncated with an ellipsis otherwise (instead of stretching the table).
    tableFixed: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '12px',
        tableLayout: 'fixed',
    },
    th: {
        textAlign: 'left',
        padding: '6px 8px',
        borderBottom: '1px solid #e2e8f0',
        color: '#64748b',
        fontWeight: 600,
    },
    thKey: {
        textAlign: 'left',
        padding: '6px 8px',
        borderBottom: '1px solid #e2e8f0',
        color: '#64748b',
        fontWeight: 600,
        width: '190px',
    },
    thBackend: {
        textAlign: 'left',
        padding: '6px 8px',
        borderBottom: '1px solid #e2e8f0',
        overflow: 'hidden',
    },
    thStatus: {
        textAlign: 'left',
        padding: '6px 8px',
        borderBottom: '1px solid #e2e8f0',
        color: '#64748b',
        fontWeight: 600,
        width: '80px',
    },
    backendPillTruncate: {
        display: 'inline-block',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        verticalAlign: 'bottom',
        fontSize: '12px',
        fontWeight: 600,
        padding: '4px 8px',
        borderRadius: '999px',
        color: '#1d4ed8',
        backgroundColor: '#dbeafe',
        fontFamily: 'monospace',
    },
    cellPresent: {
        padding: '6px 8px',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'top',
        fontFamily: 'monospace',
        color: '#0f172a',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    cellDiffers: {
        padding: '6px 8px',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'top',
        fontFamily: 'monospace',
        color: '#92400e',
        backgroundColor: '#fffbeb',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    cellMissing: {
        padding: '6px 8px',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'top',
        color: '#b91c1c',
        backgroundColor: '#fef2f2',
        textAlign: 'center',
    },
    tdExpanded: {
        padding: '8px',
        borderBottom: '1px solid #e2e8f0',
        backgroundColor: '#f8fafc',
    },
    perBackendGrid: {
        display: 'grid',
        gap: '8px',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    },
    perBackendBox: {
        display: 'grid',
        gap: '6px',
        padding: '8px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        minWidth: 0,
    },
    rowToggle: {
        background: 'none',
        border: 'none',
        padding: '0 6px 0 0',
        cursor: 'pointer',
        color: '#64748b',
        fontFamily: 'inherit',
        fontSize: '12px',
    },
    sectionActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
    },
    viewToggleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    tdKey: {
        padding: '6px 8px',
        borderBottom: '1px solid #f1f5f9',
        fontFamily: 'monospace',
        color: '#0f172a',
        fontWeight: 600,
        verticalAlign: 'top',
        wordBreak: 'break-all',
        width: '30%',
    },
    td: {
        padding: '6px 8px',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'top',
        wordBreak: 'break-all',
    },
    primitive: {
        fontFamily: 'monospace',
        color: '#0f172a',
    },
    objectView: {
        display: 'grid',
        gap: '6px',
    },
    fieldGrid: {
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        columnGap: '12px',
        rowGap: '2px',
        alignItems: 'start',
    },
    fieldLabel: {
        color: '#64748b',
    },
    fieldValue: {
        fontFamily: 'monospace',
        color: '#0f172a',
    },
    pre: {
        margin: 0,
        padding: '6px 8px',
        fontSize: '11px',
        backgroundColor: '#f1f5f9',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
    },
    linkButton: {
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: '#1d4ed8',
        fontSize: '11px',
        textAlign: 'left',
        justifySelf: 'start',
        fontFamily: 'inherit',
    },
    chipsWrap: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
    },
    chip: {
        fontFamily: 'monospace',
    },
    paginationRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '8px',
    },
    muted: {
        fontSize: '12px',
        color: '#64748b',
    },
    emptyPill: {
        display: 'inline-block',
        fontSize: '12px',
        color: '#64748b',
        borderRadius: '999px',
        padding: '4px 10px',
        backgroundColor: '#f1f5f9',
        border: '1px solid #e2e8f0',
    },
};

/**
 * Admin / debug component displaying the content of the two shared memory services
 * (All RA and Submit PR). Each service is fetched on demand through its own button.
 *
 * A `Service.get` is answered by a single backend. For the All RA memory, the answering backend acts as
 * coordinator and gathers the snapshots of the other replicas (ALL_MEMORIES_ALL_BACKENDS topic), so that
 * the view covers every backend; the "expected replicas" field tells it how many replies to wait for.
 */
export default class DisplaySharedMemoryStatus extends ReactBrick {
    static getReactComponent($) {
        /**
         * Fetch the whole content of the All RA shared memory from every backend.
         *
         * @param {number} expectedReplicas number of backends expected to answer (the coordinator stops waiting once reached)
         * @return {Promise<AllBackendsSharedMemoryData>}
         */
        const getSharedMemoryStatusAllRa = (expectedReplicas) => {
            return Service.get('allRaSharedMemory', {topic: SHARED_MEMORY_TOPICS.ALL_MEMORIES_ALL_BACKENDS, expectedReplicas})
                .then(/**
                 * @param {AllBackendsSharedMemoryData} data
                 * @return {AllBackendsSharedMemoryData}
                 */
                    (data) => {
                        return data;
                    });
        };

        /**
         * Fetch the set of PR tags currently being submitted from the Submit PR shared memory.
         *
         * @return {Promise<SubmitPRSharedMemoryData>}
         */
        const getSharedMemoryStatusSubmitPR = () => {
            return Service.get('submitPRSharedMemory', {topic: SUBMIT_PR_SHARED_MEMORY_TOPICS.PRS_BEING_SUBMITTED})
                .then(/**
                 * @param {SubmitPRSharedMemoryData} data
                 * @return {SubmitPRSharedMemoryData}
                 */
                    (data) => {
                        return data;
                    });
        };

        return () => {
            const [allRa, fetchAllRa] = useFetchState(getSharedMemoryStatusAllRa);
            const [submitPr, fetchSubmitPr] = useFetchState(getSharedMemoryStatusSubmitPR);
            // Kept as a string so the field can be emptied while typing; sanitized when fetching.
            const [expectedReplicas, setExpectedReplicas] = useState(String(DEFAULT_EXPECTED_REPLICAS));
            // 'byBackend': one card per backend listing its memories; 'byMemory': one section per memory
            // listing its keys across all backends.
            const [view, setView] = useState('byBackend');

            /** @type {?AllBackendsSharedMemoryData} */
            const allRaData = allRa.data;
            /** @type {?SubmitPRSharedMemoryData} */
            const submitPrData = submitPr.data;

            // Backends sorted with the coordinator first, then by id for a stable order between fetches.
            const backends = useMemo(() => {
                const entries = /** @type {Array<[string, SharedMemoryStatusAllRaData]>} */ (toEntries(allRaData?.backends));
                return entries.sort(([a], [b]) => {
                    if (a === allRaData?.coordinatorId) {
                        return -1;
                    }
                    if (b === allRaData?.coordinatorId) {
                        return 1;
                    }
                    return a.localeCompare(b);
                });
            }, [allRaData]);

            /**
             * Trigger the All RA fetch with the sanitized "expected replicas" value from the field.
             */
            const onFetchAllRa = () => {
                // An empty field (Number('') === 0) must fall back to the default rather than being clamped to 1.
                const parsed = expectedReplicas.trim() === '' ? NaN : Math.floor(Number(expectedReplicas));
                const sanitized = Math.min(Math.max(Number.isFinite(parsed) ? parsed : DEFAULT_EXPECTED_REPLICAS, 1), MAX_EXPECTED_REPLICAS);
                setExpectedReplicas(String(sanitized));
                fetchAllRa(sanitized);
            };

            const replicasField = (
                <TextField
                    size="small"
                    type="number"
                    label="Expected replicas"
                    value={expectedReplicas}
                    onChange={(e) => setExpectedReplicas(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !allRa.loading && onFetchAllRa()}
                    inputProps={{'min': 1, 'max': MAX_EXPECTED_REPLICAS, 'aria-label': 'Expected replicas'}}
                    style={styles.replicasField}
                />
            );

            return (
                <div style={styles.root}>
                    <div style={styles.content}>
                        <MemoryCard
                            title="All RA shared memory"
                            buttonLabel="Fetch All RA"
                            state={allRa}
                            onFetch={onFetchAllRa}
                            controls={replicasField}
                        >
                            {allRaData && <ConsistencySummary result={allRaData} backends={backends}/>}
                            {allRaData && (
                                <div style={styles.viewToggleRow}>
                                    <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, value) => value && setView(value)}>
                                        <ToggleButton value="byBackend">By backend</ToggleButton>
                                        <ToggleButton value="byMemory">By memory</ToggleButton>
                                    </ToggleButtonGroup>
                                    <span style={styles.muted}>
                                        {view === 'byBackend' ?
                                            'Each card lists the memories of one backend.' :
                                            'Each section lists the keys of one memory and which backends hold them.'}
                                    </span>
                                </div>
                            )}
                            {view === 'byBackend' && backends.map(([backendId, data]) => (
                                <BackendCard
                                    key={backendId}
                                    backendId={backendId}
                                    data={data}
                                    isCoordinator={backendId === allRaData?.coordinatorId}
                                />
                            ))}
                            {view === 'byMemory' && ALL_RA_MEMORY_NAMES.map((name) => (
                                <MemoryAcrossBackendsSection key={name} name={name} backends={backends}/>
                            ))}
                        </MemoryCard>

                        <MemoryCard
                            title="Submit PR shared memory"
                            buttonLabel="Fetch Submit PR"
                            state={submitPr}
                            onFetch={fetchSubmitPr}
                        >
                            <SetSection title="prsBeingSubmitted" raw={submitPrData?.prsBeingSubmitted}/>
                        </MemoryCard>
                    </div>
                </div>
            );
        };
    }
}

registerBrick('01a159471a5f06c2c4cb', DisplaySharedMemoryStatus);
